import React from 'react';

/**
 * Global Error Boundary — production-hardened for iOS WebView compatibility.
 *
 * Key behaviours:
 *  1. ChunkLoadError (lazy-import failure): auto-retry once, then show retry UI
 *  2. iOS in-app browser detected: show "Open in Safari" guidance
 *  3. Auth/recoverable errors: show login / homepage CTA (never a dead end)
 *  4. Reports errors to /api/log-client-error
 */

function detectIOSInApp() {
  try {
    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isSocial = /FBAN|FBAV|Instagram|Twitter|LinkedInApp|BytedanceWebview|TikTok/.test(ua);
    return { isIOS, isSocial, isIOSInApp: isIOS && isSocial };
  } catch { return { isIOS: false, isSocial: false, isIOSInApp: false }; }
}

function isChunkLoadError(error) {
  if (!error) return false;
  const msg = error.message || error.toString();
  return (
    msg.includes('Loading chunk') ||
    msg.includes('ChunkLoadError') ||
    msg.includes('Loading CSS chunk') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    (error.name === 'ChunkLoadError')
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, retried: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Auto-retry chunk load errors once
    if (isChunkLoadError(error) && !this.state.retried) {
      this.setState({ retried: true, hasError: false, error: null, errorInfo: null });
      window.location.reload();
      return;
    }

    // Report to backend
    try {
      const { isIOS, isSocial } = detectIOSInApp();
      const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      fetch(`${API}/api/log-client-error`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'ErrorBoundary',
          message: error?.toString(),
          stack: error?.stack?.substring(0, 2000),
          componentStack: errorInfo?.componentStack?.substring(0, 1000),
          url: window.location.href,
          userAgent: navigator.userAgent,
          isIOS,
          isSocialInApp: isSocial,
          isChunkError: isChunkLoadError(error),
          timestamp: new Date().toISOString(),
        }),
      }).catch(() => {});
    } catch (_) { /* ignore */ }
  }

  handleRetry = () => this.setState({ hasError: false, error: null, errorInfo: null });
  handleRefresh = () => window.location.reload();
  handleGoHome = () => { window.location.href = '/'; };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { isIOSInApp } = detectIOSInApp();
    const isChunk = isChunkLoadError(this.state.error);

    // --- Inline styles (no Tailwind / CSS dependency) ---
    const page = {
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: '#f9fafb', padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    };
    const card = {
      maxWidth: '420px', width: '100%', backgroundColor: '#fff',
      borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      textAlign: 'center',
    };
    const iconWrap = {
      width: 56, height: 56, borderRadius: '50%', backgroundColor: isChunk ? '#fef3c7' : '#fef2f2',
      display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
    };
    const heading = { fontSize: '18px', fontWeight: 700, color: '#111827', margin: '0 0 8px' };
    const para = { fontSize: '14px', color: '#6b7280', margin: '0 0 20px', lineHeight: 1.5 };
    const primaryBtn = {
      width: '100%', padding: '12px 20px', backgroundColor: '#f97316', color: '#fff',
      border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
    };
    const secondaryBtn = {
      width: '100%', padding: '12px 20px', backgroundColor: '#fff', color: '#374151',
      border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '14px', fontWeight: 500,
      cursor: 'pointer', marginTop: '10px',
    };
    const linkBtn = {
      width: '100%', padding: '10px', background: 'none', border: 'none',
      color: '#6b7280', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline',
      marginTop: '6px',
    };
    const safariBox = {
      backgroundColor: '#1e293b', color: '#f1f5f9', borderRadius: '10px',
      padding: '14px 16px', margin: '16px 0', textAlign: 'left', fontSize: '13px', lineHeight: 1.5,
    };

    return (
      <div style={page}>
        <div style={card}>
          {/* Icon */}
          <div style={iconWrap}>
            {isChunk ? (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            ) : (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            )}
          </div>

          {/* Heading */}
          <h1 style={heading}>
            {isChunk ? 'Page failed to load' : 'Something went wrong'}
          </h1>

          {/* Body */}
          <p style={para}>
            {isChunk
              ? 'A temporary network issue prevented the page from loading. Tap below to try again.'
              : 'An unexpected error occurred. Please try refreshing or go to the homepage.'}
          </p>

          {/* iOS in-app browser guidance */}
          {isIOSInApp && (
            <div style={safariBox}>
              <p style={{ margin: '0 0 6px', fontWeight: 600, color: '#f97316', fontSize: '13px' }}>
                Tip: Open in Safari for a better experience
              </p>
              <p style={{ margin: 0, color: '#94a3b8', fontSize: '12px' }}>
                Tap <strong style={{ color: '#f1f5f9' }}>&#8943;</strong> (menu) at the bottom &rarr; <strong style={{ color: '#f1f5f9' }}>Open in Safari</strong>
              </p>
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <button onClick={this.handleRefresh} style={primaryBtn} data-testid="error-refresh-btn">
              {isChunk ? 'Reload Page' : 'Refresh Page'}
            </button>
            <button onClick={this.handleGoHome} style={secondaryBtn} data-testid="error-home-btn">
              Go to Homepage
            </button>
            <button onClick={this.handleRetry} style={linkBtn}>
              Try again without refreshing
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
